import React, { useCallback } from 'react';
import block from 'bem-cn';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useGeneratePassword from 'hooks/useGeneratePassword';

import Button from 'components/Button';
import Input from 'components/Input';
import Switcher from 'components/Switcher';
import SVG from 'components/SVG';

import useCreateAgent from 'features/users/model/useCreateAgent';

import shieldSVG from '../img/shield.svg';

import './CreateAgent.scss';

const b = block('create-agent');

const CreateAgent = () => {
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const authSemiAutoWithdrawalEnabled = useSelector(state => state.auth.semiAutoWithdrawalEnabled);
  const history = useHistory();

  const {
    output: { form, disabled, withdrawalAllowed, depositAllowed, onlyPlayerCreationAllowed, semiAutomaticPayment },
    input: {
      setSemiAutomaticPayment,
      setWithdrawalAllowed,
      setDepositAllowed,
      setOnlyPlayerCreationAllowed,
      changeCommission,
      changeValue,
      changeValueMultiple,
      onSubmit,
      changeValueUsername,
      openModal,
      closeModal,
    },
  } = useCreateAgent();

  const generatePassword = useGeneratePassword(true);

  const onGeneratePassword = useCallback(() => {
    const generatedPassword = generatePassword();
    changeValueMultiple({ password: generatedPassword, confirmPassword: generatedPassword });
  }, [generatePassword]);

  // const openPopUp = useCallback(
  //   e => {
  //     e.preventDefault();
  //     openModal({
  //       content: (
  //         <div className={b('confirmation-pop-up')}>
  //           <div
  //             className={b('confirmation-pop-up', 'description')}
  //             dangerouslySetInnerHTML={{
  //               __html: `${locale.wantToCreateAgent} <span> ${form.username}</span>?`,
  //             }}
  //           />
  //           <div className={b('confirmation-pop-up', 'buttons')}>
  //             <Button color="transparent" size="low" callBack={onSubmit} text={locale.createAgent} />
  //             <Button callBack={closeModal} text={locale.cancel} size="low" />
  //           </div>
  //         </div>
  //       ),
  //     });
  //   },
  //   [closeModal, locale, openModal, onSubmit, form],
  // );

  return (
    <div className={b()}>
      <form className={b('form')} onSubmit={onSubmit}>
        <div className={b('header')}>{locale.createAgent}</div>
        <div className={b('fields')}>
          <div className={b('fields', 'left')}>
            <div className={b('form', 'input-block', { required: true })}>
              <div className={b('input')}>
                <span className={b('form', 'input-block-title')}>{locale.username}</span>
                <Input
                  validState={form.username.length > 0 ? 1 : 3}
                  placeholder={locale.username}
                  onChange={changeValueUsername}
                  value={form.username}
                />
              </div>
            </div>

            <div className={b('generate')}>
              <Button
                text={
                  <>
                    <span className={b('generate-text')}>{locale.generateStrongPassword}</span>
                    <SVG className={b('generate-icon')} svgProps={{ svg: shieldSVG }} />
                  </>
                }
                color="generate-password"
                callBack={onGeneratePassword}></Button>
            </div>
            <div className={b('form', 'input-block', { required: true })}>
              <span className={b('form', 'input-block-title')}>{locale.password}</span>
              <div className={b('input')}>
                <Input
                  placeholder={locale.password}
                  withEye
                  validState={form.password.length > 0 ? 1 : 3}
                  type="password"
                  onChange={changeValue}
                  value={form.password}
                  name="password"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className={b('form', 'input-block', { required: true })}>
              <span className={b('form', 'input-block-title')}>{locale.confirmPassword}</span>
              <div className={b('input')}>
                <Input
                  placeholder={locale.confirmPassword}
                  onChange={changeValue}
                  value={form.confirmPassword}
                  name="confirmPassword"
                  withEye
                  type="password"
                  validState={form.confirmPassword !== form.password ? 3 : 1}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className={b('form', 'input-block', { type: 'commission', required: true })}>
              <span className={b('form', 'input-block-title')}>{locale.commissionBalance} %</span>
              <div className={b('input')}>
                <Input
                  placeholder={`${locale.commissionBalance} %`}
                  onChange={changeCommission}
                  value={form.commission}
                  name="commission"
                />
              </div>
            </div>
          </div>
          <div className={b('fields', 'right')}>
            <div className={b('form', 'input-block')}>
              <div className={b('input')}>
                <span className={b('form', 'input-block-title')}>{locale.email}</span>
                <Input placeholder={locale.email} onChange={changeValue} value={form.email} name="email" />
              </div>
            </div>
            <div className={b('form', 'input-block')}>
              <span className={b('form', 'input-block-title')}>{locale.name}</span>
              <div className={b('input')}>
                <Input placeholder={locale.name} onChange={changeValue} value={form.name} name="name" />
              </div>
            </div>
            <div className={b('form', 'input-block')}>
              <span className={b('form', 'input-block-title')}>{locale.surname}</span>
              <div className={b('input')}>
                <Input placeholder={locale.surname} onChange={changeValue} value={form.surname} name="surname" />
              </div>
            </div>

            <div className={b('form', 'switchers')}>
              <div className={b('form', 'switchers-block')}>
                <div
                  className={b('form', 'switchers-title')}
                  dangerouslySetInnerHTML={{ __html: locale.depositAllowed }}
                />
                <div className={b('form', 'switchers-item')}>
                  <Switcher key="depositAllowed" isActive={depositAllowed} onSwitch={setDepositAllowed} />
                </div>
              </div>

              <div className={b('form', 'switchers-block')}>
                <div
                  className={b('form', 'switchers-title')}
                  dangerouslySetInnerHTML={{ __html: locale.withdrawalAllowed }}
                />
                <div className={b('form', 'switchers-item')}>
                  <Switcher key="withdrawalAllowed" isActive={withdrawalAllowed} onSwitch={setWithdrawalAllowed} />
                </div>
              </div>

              <div className={b('form', 'switchers-block')}>
                <div
                  className={b('form', 'switchers-title')}
                  dangerouslySetInnerHTML={{ __html: locale.onlyPlayerCreationAllowed }}
                />
                <div className={b('form', 'switchers-item')}>
                  <Switcher
                    key="onlyPlayerCreationAllowed"
                    isActive={onlyPlayerCreationAllowed}
                    onSwitch={setOnlyPlayerCreationAllowed}
                  />
                </div>
              </div>

              {authSemiAutoWithdrawalEnabled && (
                <div className={b('form', 'switchers-block')}>
                  <div
                    className={b('form', 'switchers-title')}
                    dangerouslySetInnerHTML={{ __html: locale.semiAutoWithdrawalEnabled }}
                  />

                  <div className={b('form', 'switchers-item')}>
                    <Switcher
                      key="semiAutomaticPayment"
                      isActive={semiAutomaticPayment}
                      onSwitch={setSemiAutomaticPayment}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={b('bottom')}>
          <Button color="full-transparent" callBack={history.goBack} text={locale.cancel} size="low" />
          <Button disabled={disabled} type="submit" size="low" text={locale.createAgent} />
        </div>
      </form>
    </div>
  );
};

export default CreateAgent;
